import { ReactNode } from 'react';
import { Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@chakra-ui/react';
import { ChevronRight } from 'lucide-react';
import { Link as RouterLink } from 'react-router-dom';
import { iconSize } from '@duke-hq/ui/constants/scalable';

export type Crumb = { label: string; to: string };

/**
 * Breadcrumb trail for an editor's secondary header. Parent crumbs are links;
 * the final crumb is the current page, rendered as the `current` node (typically
 * the editable title) rather than a link.
 *
 * The trail never wraps or truncates: it's a single-line strip that scrolls
 * horizontally when it's wider than the bar (e.g. a long title on a narrow
 * viewport), so no crumb is lost. It stays left-aligned, so the unit and the
 * start of the title are what you see first, and you scroll right for the rest.
 */
export function EditorBreadcrumbs({ crumbs, current }: { crumbs: Crumb[]; current: ReactNode }) {
  return (
    <Box
      flex="1"
      minW={0}
      overflowX="auto"
      // The scrollbar would eat into the slim bar's height; the clipped crumb at
      // the edge is enough of a cue that the trail scrolls.
      sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}
    >
      <Breadcrumb
        separator={<ChevronRight size={iconSize.sm} color="currentColor" />}
        fontSize="sm"
        color="fg.muted"
        spacing="1.5"
        // Natural width so the scroll container (the parent Box) sees the full
        // trail and scrolls, rather than the trail being squeezed to fit.
        w="max-content"
        sx={{ '& > ol': { display: 'flex', alignItems: 'center', flexWrap: 'nowrap' } }}
      >
        {crumbs.map((crumb) => (
          <BreadcrumbItem key={crumb.to} flexShrink={0}>
            <BreadcrumbLink as={RouterLink} to={crumb.to} whiteSpace="nowrap">
              {crumb.label}
            </BreadcrumbLink>
          </BreadcrumbItem>
        ))}
        <BreadcrumbItem isCurrentPage flexShrink={0}>
          {current}
        </BreadcrumbItem>
      </Breadcrumb>
    </Box>
  );
}
